import { decodeQr } from './decode';
import type { ScanListener, ScanResult } from './types';

const PROCESS_WIDTH = 480;
const PROCESS_HEIGHT = 360;
const DECODE_INTERVAL_MS = 150; // ~6-7 decode attempts/sec, gentle on tablet CPUs
const RESCAN_COOLDOWN_MS = 2000; // avoid re-triggering the same card repeatedly

export class ScanController {
  private stream: MediaStream | null = null;
  private readonly canvas = document.createElement('canvas');
  private readonly ctx: CanvasRenderingContext2D;
  private readonly listeners = new Set<ScanListener>();
  private intervalId: number | null = null;
  private lastValue: string | null = null;
  private lastValueAt = 0;

  constructor(private readonly video: HTMLVideoElement) {
    this.canvas.width = PROCESS_WIDTH;
    this.canvas.height = PROCESS_HEIGHT;
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Could not create QR processing canvas context.');
    this.ctx = ctx;
  }

  onScan(listener: ScanListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async start(): Promise<void> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
    } catch (error) {
      this.emit({ status: 'error', message: `카메라를 켤 수 없어요: ${(error as Error).message}` });
      return;
    }

    this.video.srcObject = this.stream;
    this.video.setAttribute('playsinline', 'true');
    this.video.muted = true;
    await this.video.play();

    this.intervalId = window.setInterval(() => this.tick(), DECODE_INTERVAL_MS);
  }

  stop(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
    this.video.srcObject = null;
  }

  dispose(): void {
    this.stop();
    this.listeners.clear();
  }

  private tick(): void {
    if (this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;

    this.ctx.drawImage(this.video, 0, 0, PROCESS_WIDTH, PROCESS_HEIGHT);
    const imageData = this.ctx.getImageData(0, 0, PROCESS_WIDTH, PROCESS_HEIGHT);
    const value = decodeQr(imageData);
    if (!value) return;

    const now = performance.now();
    if (value === this.lastValue && now - this.lastValueAt < RESCAN_COOLDOWN_MS) return;

    this.lastValue = value;
    this.lastValueAt = now;
    this.emit({ status: 'detected', qrValue: value });
  }

  private emit(result: ScanResult): void {
    for (const listener of this.listeners) listener(result);
  }
}
